import { AppAvatar } from '@components/app-avatar';
import { truncateString } from '@modules/settings/subscription/utils/utils';
import { useSupportChat } from '@shared/hooks/use-support-chat';
import { useUserGuideStore } from '@shared/store/user-guide.store';
import { isPublicRoute } from '@shared/utils';
import { capitalize } from '@shared/utils/capitalize';
import { Button, Popover, Skeleton, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  RiArrowRightSLine,
  RiArrowRightUpLine,
  RiBookOpenLine,
  RiCircleFill,
  RiCoupon2Line,
  RiCustomerService2Fill,
  RiLogoutBoxRLine,
  RiMoonFill,
  RiPencilLine,
  RiVerifiedBadgeLine,
} from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDoNotDisturb } from '../hooks/use-do-not-disturb';
import { useProfile } from '../hooks/use-profile-dropdown';
import { definedTime } from '../utils/dnd-constants';
import { DefinedTimeContent } from './defined-time-content';
import { DndModal } from './dnd-modal';
import { ModalLogout } from './logout-modal';
import { SwitchWorkspaceMenuItem } from './switch-workspace-menu-item';
import { useworkspaceAuthMeV2Query } from '@/types/generated/graphql';
import { cacheOnly } from '@shared/constants';

interface ProfileContentProps {
  closeDropdown: () => void;
}

export function ProfileContent({ closeDropdown }: ProfileContentProps) {
  const {
    open,
    control,
    handleSubmit,
    statusValue,
    onSubmit,
    loading: userStatusLoading,
    handleDurationSelect,
    handleSuggestedStatus,
    handleDateChange,
    handleTimeChange,
    handleModalClose,
    setEmoji,
    handleOpen,
    selectedDateTime,
    setMessage,
    emoji,
    userActiveStatus,
    updatingUserStatus,
    handleSetUserActive,
    awayStatusMessage,
    icon,
    time: awayTimeRemaining,
    timeLabel,
    awayTime,
    timezoneDate,
  } = useDoNotDisturb();
  const { userName, profileUrl, loading, handleLogout, userRole, logoutHandleOpen, logoutOpen, logoutHandleClose } =
    useProfile();
  const { data: workspaceAuthMe } = useworkspaceAuthMeV2Query(cacheOnly);
  const isKycVerified = !!workspaceAuthMe?.workspaceAuthMeV2?.data?.kycVerified;
  const location = useLocation();
  const { openSupportChat } = useSupportChat();
  const { setOpenUserGuide } = useUserGuideStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="w-[230px] rounded-xl bg-white font-medium">
      <div className="flex items-center gap-2 p-2 border-b border-gray-200">
        <span className="inline-flex shrink-0">
          <AppAvatar size={36} src={profileUrl} className="border border-gray-200! shrink-0" name={userName} />
        </span>
        <div>
          <p className="text-[15px] text-text-700 leading-none mb-0.5 inline-flex items-center gap-1">
            {truncateString(userName, 15)} {!userActiveStatus && icon}
            {isKycVerified && <RiVerifiedBadgeLine className="text-emerald-500 size-4" />}
          </p>
          {isPublicRoute(location.pathname) ? (
            <p className="text-xs text-text-600 font-normal leading-none">{capitalize(t('owner'))}</p>
          ) : (
            <p className="text-xs text-text-600 font-normal leading-none cursor-pointer">
              {userActiveStatus ? (
                <span>{userRole}</span>
              ) : (
                <Tooltip placement="top" title={<p className="text-xs text-slate-500">{awayStatusMessage}</p>}>
                  {userStatusLoading ? (
                    <Skeleton active paragraph={false} />
                  ) : (
                    `${truncateString(awayStatusMessage, 12)}, ${awayTimeRemaining}`
                  )}
                </Tooltip>
              )}
            </p>
          )}
        </div>
      </div>
      {isPublicRoute(location.pathname) && <SwitchWorkspaceMenuItem />}
      {!isPublicRoute(location.pathname) && (
        <div className="text-sm! font-medium! px-1! pt-1!">
          <div className="space-y-1.5">
            {!userActiveStatus && !userStatusLoading && (
              <>
                <Button
                  onClick={handleSetUserActive}
                  loading={updatingUserStatus}
                  type="text"
                  className="w-full justify-start! gap-2! rounded-md pl-3! h-8!"
                  icon={<RiCircleFill color="var(--color-green-700)" size={13} />}
                >
                  {t('set_yourself_as_active')}
                </Button>
                <hr className="text-gray-400" />
              </>
            )}

            <Popover
              className="p-0 mr-10!"
              destroyOnHidden
              rootClassName="logout__popover"
              align={{ offset: [-5, 30] }}
              content={
                <DefinedTimeContent
                  awayTime={awayTime}
                  timeLabel={timeLabel}
                  userActiveStatus={userActiveStatus}
                  profileUrl={profileUrl}
                  userName={userName}
                  message={awayStatusMessage}
                  awayStatus={String(awayTimeRemaining)}
                  definedTime={definedTime(t)}
                  handleDurationSelect={handleDurationSelect}
                  handleOpen={handleOpen}
                  icon={icon}
                />
              }
              trigger="click"
              placement="left"
              arrow={false}
            >
              <Button
                type="text"
                className="w-full justify-start! rounded-md pl-3! h-8! min-w-0!"
                icon={<RiMoonFill className="text-amber-600!" size={13} />}
              >
                <span className="flex items-center gap-2 w-full min-w-0">
                  <span className="truncate flex-1">{t('set_yourself_as_away')}</span>
                  <RiArrowRightSLine className="ml-auto shrink-0" />
                </span>
              </Button>
            </Popover>
          </div>
          <div>
            <Button
              icon={<RiPencilLine size={18} style={{ verticalAlign: 'middle' }} />}
              className="w-full justify-start! rounded-md pl-1.5!"
              type="text"
              onClick={() => navigate('/settings/my-profile')}
            >
              {capitalize(t('edit_profile'))}
            </Button>
            <Button
              data-tour-id="user_guide"
              icon={<RiBookOpenLine size={18} style={{ verticalAlign: 'middle' }} />}
              className="w-full justify-start! rounded-md pl-1.5!"
              type="text"
              onClick={() => {
                closeDropdown();
                setOpenUserGuide(true);
              }}
            >
              {capitalize(t('user_guide'))}
            </Button>
            <Button
              icon={
                <RiCustomerService2Fill
                  size={18}
                  className="text-text-800 cursor-pointer"
                  onClick={openSupportChat}
                  style={{ verticalAlign: 'middle' }}
                />
              }
              className=" w-full justify-start! rounded-md pl-1.5!"
              type="text"
              onClick={openSupportChat}
            >
              {capitalize(t('support_chat'))}
            </Button>
            <Button
              type="text"
              className="w-full justify-start! rounded-md pl-1.5! min-w-0!"
              icon={<RiCoupon2Line size={18} style={{ verticalAlign: 'middle' }} />}
              onClick={() => window.open(window.location.origin + '/support-ticket')}
            >
              <span className="flex items-center gap-2 w-full min-w-0!">
                <span title={t('raise_a_support_ticket')} className="truncate flex-1 text-left">
                  {t('raise_a_support_ticket')}
                </span>
                <RiArrowRightUpLine size={18} className="shrink-0" />
              </span>
            </Button>
          </div>
        </div>
      )}
      <hr className="text-text-50" />
      <div className="text-sm! font-medium! px-1.5!">
        <Button
          type="text"
          className="text-left w-full justify-start! rounded-md pl-1! pb-0.5!"
          icon={
            <RiLogoutBoxRLine
              className="ml-auto! font-semibold!"
              size={18}
              style={{ verticalAlign: 'middle' }}
              color="var(--color-red-600)"
            />
          }
          onClick={logoutHandleOpen}
        >
          {capitalize(t('logout'))}
        </Button>
      </div>
      <DndModal
        handleModalClose={handleModalClose}
        open={open}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        control={control}
        statusValue={statusValue}
        handleDateChange={handleDateChange}
        selectedDateTime={selectedDateTime}
        handleTimeChange={handleTimeChange}
        setMessage={setMessage}
        emoji={emoji}
        handleSuggestedStatus={handleSuggestedStatus}
        updatingUserStatus={updatingUserStatus}
        timezoneDate={timezoneDate}
        setEmoji={setEmoji}
      />
      <ModalLogout
        open={logoutOpen}
        handleModalClose={logoutHandleClose}
        handleLogout={handleLogout}
        loading={loading}
      />
    </div>
  );
}
